import { useQuery } from '@tanstack/react-query';
import { getOperationDetails } from '@/shared/api';

interface UseOperationDetailsOptions<T> {
  operationId: string;
  enabled?: boolean;
  refetchInterval?: number;
  shouldPollUntil?: (data: T) => boolean;
}

export function useOperationDetails<T>({
  operationId,
  enabled = true,
  refetchInterval = 5000,
  shouldPollUntil,
}: UseOperationDetailsOptions<T>) {
  const queryKey = ['operationDetails', operationId];

  const query = useQuery<T, Error>({
    queryKey,
    queryFn: async () => {
      return await getOperationDetails(operationId);
    },
    enabled: enabled && !!operationId,
    staleTime: 0,
    gcTime: 0,
    retry: 3,
    refetchInterval: query => {
      if (!enabled) return false;

      if (!shouldPollUntil) return refetchInterval;

      if (!query.state.data) return refetchInterval;

      if (!shouldPollUntil(query.state.data)) return false;

      return refetchInterval;
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    status: query.status,
    refetch: query.refetch,
    query,
  };
}
